import React from 'react';
import {
  MAX_MATERIAL_CHARS,
  MAX_MATERIALS,
  MAX_MATERIALS_TOTAL_CHARS,
  VOICES,
  DURATIONS,
  type AccentId,
  type CandidateBehavior,
  type CandidateProfileId,
  type DurationMinutes,
  type InterviewLanguage,
  type InterviewSetup,
  type MaterialKind,
  type PersonaId,
  type ScenarioId,
  type VoiceEngine,
  type VoiceId,
} from '../shared/contract';
import { InAppBanner, TaniaCard } from './components';
import { IN_APP_MESSAGE, isInAppBrowser } from './inapp';
import { cassieBackgroundText, profileSummary, useContent } from './content';
import { LockIcon, MicIcon } from './icons';
import { ACCEPTED_UPLOADS, readMaterialFile, totalChars, type UploadedMaterial } from './materials';
import {
  ACCENT_OPTIONS,
  BEHAVIORS,
  CANDIDATE_PROFILE_OPTIONS,
  DIFFICULTY_OPTIONS,
  MATERIAL_KIND_LABELS,
  PERSONAS,
  SCENARIOS,
  LIVE_VOICES,
  UPLOAD_KINDS,
  VOICE_ENGINE_OPTIONS,
  VOICE_LABELS,
  isRolePlay,
  type Option,
} from './types';

function SegmentedControl<T extends string>({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: T) => void;
  options: ReadonlyArray<Option<T>>;
  value: T;
}) {
  return (
    <fieldset className="field-group">
      <legend>{label}</legend>
      <div className="segmented-control">
        {options.map((option) => (
          <button
            aria-pressed={option.value === value}
            className={option.value === value ? 'is-selected' : ''}
            key={option.value}
            onClick={() => onChange(option.value)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function SelectField<T extends string | number>({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: T) => void;
  options: ReadonlyArray<Option<T>>;
  value: T;
}) {
  return (
    <label className="field-group">
      <span>{label}</span>
      <select
        onChange={(event) => {
          const picked = options.find((option) => String(option.value) === event.target.value);
          if (picked) onChange(picked.value);
        }}
        value={String(value)}
      >
        {options.map((option) => (
          <option key={option.value} value={String(option.value)}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function MaterialRow({
  children,
  doc,
  onRemove,
}: {
  children?: React.ReactNode;
  doc: UploadedMaterial;
  onRemove?: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <li className="material-row">
      <div className="material-row-head">
        <button
          aria-expanded={open}
          className="material-name"
          onClick={() => setOpen((current) => !current)}
          type="button"
        >
          <strong>{doc.name}</strong>
          <small>
            {MATERIAL_KIND_LABELS[doc.kind]} · {doc.text.length.toLocaleString()} chars
          </small>
        </button>
        {doc.truncated && <span className="badge badge--warn">truncated</span>}
        {onRemove && (
          <button aria-label={`Remove ${doc.name}`} className="icon-button" onClick={onRemove} type="button">
            ×
          </button>
        )}
      </div>
      {children}
      {open && <pre className="material-viewer">{doc.text}</pre>}
    </li>
  );
}

function MaterialsPanel({
  setup,
  update,
}: {
  setup: InterviewSetup;
  update: <Key extends keyof InterviewSetup>(key: Key, value: InterviewSetup[Key]) => void;
}) {
  const content = useContent();
  const [kind, setKind] = React.useState<MaterialKind>('candidate_cv');
  const [busy, setBusy] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const [open, setOpen] = React.useState(
    () => typeof window.matchMedia !== 'function' || window.matchMedia('(min-width: 761px)').matches,
  );
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const used = totalChars(setup.materials);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setUploadError('');
    let next = setup.materials;
    const problems: string[] = [];
    for (const file of Array.from(files)) {
      try {
        next = [...next, await readMaterialFile(file, kind, next)];
      } catch (error: unknown) {
        problems.push(error instanceof Error ? error.message : `${file.name} could not be read.`);
      }
    }
    update('materials', next);
    setUploadError(problems.join(' '));
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <details
      className="materials-panel"
      onToggle={(event) => setOpen(event.currentTarget.open)}
      open={open}
    >
      <summary>
        <span>Materials</span>
        <small>
          {setup.useDefaultMaterials ? `Ideals pack + ` : ''}
          {setup.materials.length} uploaded
        </small>
      </summary>

      <label className="toggle-row">
        <input
          checked={setup.useDefaultMaterials}
          onChange={(event) => update('useDefaultMaterials', event.target.checked)}
          type="checkbox"
        />
        <span>Use Ideals pack (default)</span>
      </label>
      {setup.useDefaultMaterials && (
        <ul className="material-list">
          {(content?.defaultMaterials ?? []).map((doc) => (
            <MaterialRow doc={doc} key={doc.id} />
          ))}
          {content?.cassieProfile && !(content.defaultMaterials ?? []).some((doc) => /cassie|linkedin/iu.test(doc.name)) && (
            <MaterialRow
              doc={{ id: 'cassie-background', kind: 'my_cv', name: 'Cassie’s background (from LinkedIn)', text: cassieBackgroundText(content.cassieProfile) }}
              key="cassie-background"
            />
          )}
        </ul>
      )}

      <div className="upload-box">
        <div className="upload-controls">
          <label className="field-group">
            <span>Document type</span>
            <select onChange={(event) => setKind(event.target.value as MaterialKind)} value={kind}>
              {UPLOAD_KINDS.map((item) => (
                <option key={item} value={item}>{MATERIAL_KIND_LABELS[item]}</option>
              ))}
            </select>
          </label>
          <label className={`upload-button ${busy || setup.materials.length >= MAX_MATERIALS ? 'is-disabled' : ''}`}>
            <input
              accept={ACCEPTED_UPLOADS}
              disabled={busy || setup.materials.length >= MAX_MATERIALS}
              multiple
              onChange={(event) => void handleFiles(event.target.files)}
              ref={inputRef}
              type="file"
            />
            <span>{busy ? 'Reading…' : 'Upload files'}</span>
          </label>
        </div>
        <small className="hint">
          PDF, DOCX, TXT, MD, PNG, JPG up to 8 MB. {setup.materials.length}/{MAX_MATERIALS} files ·{' '}
          {used.toLocaleString()} / {MAX_MATERIALS_TOTAL_CHARS.toLocaleString()} chars (max{' '}
          {MAX_MATERIAL_CHARS.toLocaleString()} each).
        </small>
        {uploadError && <div className="form-error" role="alert">{uploadError}</div>}
      </div>

      {setup.materials.length > 0 && (
        <ul className="material-list">
          {setup.materials.map((doc) => (
            <MaterialRow
              doc={doc}
              key={doc.id}
              onRemove={() =>
                update('materials', setup.materials.filter((item) => item.id !== doc.id))
              }
            />
          ))}
        </ul>
      )}
    </details>
  );
}

export function Lobby({
  error,
  onPrep,
  onRetryLive,
  onStart,
  onUseRealtime,
  setup,
  setSetup,
}: {
  error: string;
  onPrep: () => void;
  onRetryLive?: () => void;
  onStart: () => Promise<void>;
  onUseRealtime?: () => void;
  setup: InterviewSetup;
  setSetup: React.Dispatch<React.SetStateAction<InterviewSetup>>;
}) {
  const update = <Key extends keyof InterviewSetup>(key: Key, value: InterviewSetup[Key]) => {
    setSetup((current) => ({ ...current, [key]: value }));
  };
  const content = useContent();
  const inApp = isInAppBrowser();
  const rolePlay = isRolePlay(setup.scenario);
  const needsCv =
    rolePlay &&
    setup.candidateProfile === 'from_materials' &&
    !setup.materials.some((material) => material.kind === 'candidate_cv');
  const scenario = SCENARIOS.find((item) => item.value === setup.scenario);
  const profileHint =
    setup.candidateProfile === 'from_materials'
      ? 'The AI plays the person in your uploaded Candidate CV.'
      : profileSummary(content, setup.candidateProfile);

  return (
    <main className="lobby-page">
      {inApp && <InAppBanner />}
      <section className="lobby-hero">
        <div className="lobby-intro">
          <p className="eyebrow">Your private practice room</p>
          <h1>Practice the call,<br /><em>not the script.</em></h1>
          <p>
            Run the Ideals screening-call role play against an AI candidate, then take Tania’s
            follow-up questions. A written debrief scores you against what the team will look at.
          </p>
          <div className="session-promise">
            <div><strong>01</strong><span>60-minute<br />role play</span></div>
            <div><strong>02</strong><span>Probe real<br />sales metrics</span></div>
            <div><strong>03</strong><span>Scored<br />debrief</span></div>
          </div>
          <TaniaCard />
          <button className="ghost-button" onClick={onPrep} type="button">Open interview prep</button>
        </div>

        <form
          className="setup-card"
          onSubmit={(event) => {
            event.preventDefault();
            void onStart();
          }}
        >
          <div className="setup-heading">
            <div>
              <p className="eyebrow">Room setup</p>
              <h2>Choose the conversation</h2>
            </div>
          </div>
          {error && (
            <div className="form-error" role="alert">
              {error}
              {onRetryLive && onUseRealtime && (
                <div className="error-actions">
                  <button onClick={onRetryLive} type="button">Retry GPT-Live</button>
                  <button onClick={onUseRealtime} type="button">Use GPT-Realtime 2.1 instead</button>
                </div>
              )}
            </div>
          )}

          <fieldset className="field-group scenario-field">
            <legend>Scenario</legend>
            <div className="scenario-grid" role="radiogroup" aria-label="Scenario">
              {SCENARIOS.map((item) => (
                <button
                  aria-checked={setup.scenario === item.value}
                  className={`scenario-card ${setup.scenario === item.value ? 'is-selected' : ''}`}
                  key={item.value}
                  onClick={() =>
                    setSetup((current) => ({
                      ...current,
                      scenario: item.value as ScenarioId,
                      durationMinutes: item.value === 'roleplay_screening' ? 60 : current.durationMinutes,
                    }))}
                  role="radio"
                  type="button"
                >
                  <strong>{item.label}</strong>
                  <small>{item.short}</small>
                </button>
              ))}
            </div>
            {scenario && <p className="scenario-description">{scenario.description}</p>}
          </fieldset>

          <div className="setup-grid">
            <label className="field-group field-group--wide">
              <span>Target role</span>
              <input
                maxLength={80}
                onChange={(event) => update('role', event.target.value)}
                placeholder="e.g. Senior Backend Engineer"
                required
                value={setup.role}
              />
            </label>

            <fieldset className="field-group field-group--wide">
              <legend>Interviewer style{rolePlay ? ' (how tough Tania is)' : ''}</legend>
              <div className="persona-grid">
                {PERSONAS.map((persona) => (
                  <button
                    aria-pressed={setup.persona === persona.id}
                    className={`persona-card ${setup.persona === persona.id ? 'is-selected' : ''}`}
                    key={persona.id}
                    onClick={() => update('persona', persona.id as PersonaId)}
                    type="button"
                  >
                    <span className="persona-monogram">{persona.monogram}</span>
                    <span>
                      <strong>{persona.name}</strong>
                      <small>{persona.description}</small>
                    </span>
                    <i />
                  </button>
                ))}
              </div>
            </fieldset>
            {rolePlay && (
              <>
                <SelectField<CandidateProfileId>
                  label="AI candidate"
                  onChange={(value) => update('candidateProfile', value)}
                  options={CANDIDATE_PROFILE_OPTIONS}
                  value={setup.candidateProfile}
                />
                <SelectField<CandidateBehavior>
                  label="Candidate behavior"
                  onChange={(value) => update('candidateBehavior', value)}
                  options={BEHAVIORS}
                  value={setup.candidateBehavior}
                />
                {profileHint && <p className="field-hint field-group--wide">{profileHint}</p>}
                {needsCv && (
                  <p className="field-hint field-hint--warn field-group--wide">
                    Upload a document of type “Candidate CV” below to use this option.
                  </p>
                )}
              </>
            )}

            <SegmentedControl
              label="Difficulty"
              onChange={(value) => update('difficulty', value)}
              options={DIFFICULTY_OPTIONS}
              value={setup.difficulty}
            />
            <SegmentedControl<`${DurationMinutes}`>
              label="Duration (min)"
              onChange={(value) => update('durationMinutes', Number(value) as DurationMinutes)}
              options={DURATIONS.map((value) => ({ label: String(value), value: `${value}` as `${DurationMinutes}` }))}
              value={`${setup.durationMinutes}`}
            />
            <SegmentedControl<InterviewLanguage>
              label="Language"
              onChange={(value) => update('language', value)}
              options={[
                { label: 'English', value: 'en' },
                { label: '中文', value: 'zh' },
              ]}
              value={setup.language}
            />
            <div className="field-group--wide">
              <SelectField<AccentId>
                label="Tania’s accent"
                onChange={(value) => update('accent', value)}
                options={ACCENT_OPTIONS}
                value={setup.accent}
              />
            </div>
            <SelectField<VoiceId>
              label="AI voice"
              onChange={(value) => update('voice', value)}
              options={VOICES.filter((voice) => setup.voiceEngine !== 'gpt-live-1' || LIVE_VOICES.includes(voice)).map((voice) => ({ label: VOICE_LABELS[voice], value: voice }))}
              value={setup.voice}
            />
            <SelectField<VoiceEngine>
              label="Voice engine"
              onChange={(value) =>
                setSetup((current) => ({
                  ...current,
                  voiceEngine: value,
                  voice: value === 'gpt-live-1' && !LIVE_VOICES.includes(current.voice) ? 'marin' : current.voice,
                }))}
              options={VOICE_ENGINE_OPTIONS}
              value={setup.voiceEngine}
            />
            <label className="field-group field-group--wide">
              <span>Focus <small>optional</small></span>
              <textarea
                maxLength={600}
                onChange={(event) => update('focus', event.target.value)}
                placeholder="A topic to stress, or an instruction for the AI. Leave blank for a representative session."
                rows={3}
                value={setup.focus}
              />
            </label>
          </div>

          <MaterialsPanel setup={setup} update={update} />

          <div className="setup-footer">
            <p className="privacy-inline">{inApp ? IN_APP_MESSAGE : <><LockIcon /> Audio is not stored. Notes stay in this browser.</>}</p>
            <button
              className="primary-button primary-button--coral"
              disabled={!setup.role.trim() || needsCv || inApp}
              type="submit"
            >
              <MicIcon />
              <span>Start {rolePlay ? 'call' : 'interview'}</span>
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
